// src/components/store/sections/product-cards/LayeredStackProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Zap, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const STACK_THEMES = [
  { back1: "bg-purple-500", back2: "bg-pink-400", badge: "bg-purple-600", text: "text-purple-600" },
  { back1: "bg-cyan-500", back2: "bg-blue-400", badge: "bg-cyan-600", text: "text-cyan-600" },
  { back1: "bg-emerald-500", back2: "bg-teal-400", badge: "bg-emerald-600", text: "text-emerald-600" },
  { back1: "bg-amber-500", back2: "bg-rose-400", badge: "bg-amber-600", text: "text-amber-600" },
];

export default function LayeredStackProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    added,
    handleAddToCart,
    handleBuyNow,
  } = useCardActions(product, subdomain);

  const theme = STACK_THEMES[index % STACK_THEMES.length];

  return (
    <div className="relative w-full h-full p-2 select-none group">
      {/* Cascading Back Deck Layers */}
      <div
        className={`absolute inset-2 rounded-3xl ${theme.back2} opacity-25 rotate-3 group-hover:rotate-6 transition-transform duration-300 pointer-events-none`}
      />
      <div
        className={`absolute inset-2 rounded-3xl ${theme.back1} opacity-40 -rotate-2 group-hover:-rotate-4 transition-transform duration-300 pointer-events-none`}
      />

      {/* Foreground Card */}
      <div className="relative z-10 w-full h-full bg-white rounded-3xl border border-slate-200 p-4 shadow-lg flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 truncate">
              {categoryName}
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-white font-mono font-black text-xs ${theme.badge}`}>
              ৳{currentPrice.toLocaleString()}
            </span>
          </div>

          <Link
            href={productUrl}
            className="relative w-full h-44 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center p-3 mb-3 block"
          >
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain p-2 group-hover:scale-110 transition-transform duration-500 drop-shadow-md"
            />
          </Link>

          <Link href={productUrl} className="block">
            <h3 className="text-sm font-black text-slate-900 truncate hover:underline">
              {productName}
            </h3>
          </Link>

          <div className="flex items-center justify-between mt-1.5">
            <div className="flex items-center gap-0.5 text-amber-400">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-3 h-3 fill-amber-400" />
              ))}
              <span className="text-[10px] text-slate-400 font-bold ml-1">({rating.toFixed(1)})</span>
            </div>
            {comparePrice > currentPrice && (
              <span className="text-[10px] text-slate-400 line-through font-mono">
                ৳{comparePrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-3 mt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            className={`flex-1 py-2 rounded-xl text-white font-black text-[10px] uppercase tracking-wider shadow-sm active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1 ${
              added ? "bg-emerald-600" : "bg-slate-900 hover:bg-slate-800"
            }`}
          >
            {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
            <span>{added ? "Added" : "Add Cart"}</span>
          </button>

          <button
            type="button"
            onClick={(e) => handleBuyNow(e)}
            className={`p-2 rounded-xl text-white ${theme.badge} hover:opacity-90 active:scale-95 transition-all cursor-pointer`}
            title="Buy Now"
          >
            <Zap className="w-4 h-4 fill-current" />
          </button>
        </div>
      </div>
    </div>
  );
}
